import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getTvDashboard } from '../../services/ticketService';
import { getDemandTypeLabel } from '../../services/demandTypeService';
import { getPriorityLabel } from '../../services/priorityService';
import { formatTicketProtocol } from '../../utils/ticketDisplayUtils';
import '../../styles/pages/tvDashboard.css';

const DEFAULT_REFRESH_MS = 60_000;
const DEFAULT_ROTATE_MS = 45_000;
const DEFAULT_PERIOD_DAYS = 30;

const SLIDES = [
  {
    id: 'overview',
    title: 'Visão geral',
    subtitle: 'Bugs · Melhorias e Projetos · Outros',
  },
  {
    id: 'sla',
    title: 'SLA de bugs',
    subtitle: 'Suporte / bugs em aberto — prazos por prioridade',
  },
  {
    id: 'performers',
    title: 'Performance do time',
    subtitle: 'Pódio — top 5 responsáveis no período',
  },
];

const DEMAND_COLORS = {
  SUPPORT: '#6c8cff',
  IMPROVEMENT: '#f59e0b',
  PROJECT: '#4ade80',
  OTHER: '#a78bfa',
};

const PERFORMER_SERIES = [
  { key: 'SUPPORT', label: 'Suporte / Bug' },
  { key: 'IMPROVEMENT', label: 'Melhoria' },
  { key: 'PROJECT', label: 'Projeto' },
  { key: 'OTHER', label: 'Outros' },
];

const TV_PODIUM_LIMIT = 5;

const PODIUM_RANK_META = [
  { className: 'tv-podium-row--gold', medal: '🥇' },
  { className: 'tv-podium-row--silver', medal: '🥈' },
  { className: 'tv-podium-row--bronze', medal: '🥉' },
];

const OVERVIEW_GROUPS = [
  {
    id: 'bugs',
    title: 'Bugs',
    subtitle: 'Suporte / correções',
    types: ['SUPPORT'],
    accent: '#6c8cff',
  },
  {
    id: 'improvement-project',
    title: 'Melhorias e Projetos',
    subtitle: 'Evolução e entregas',
    types: ['IMPROVEMENT', 'PROJECT'],
    accent: '#4ade80',
  },
  {
    id: 'other',
    title: 'Outros',
    subtitle: 'Demandas diversas',
    types: ['OTHER'],
    accent: '#a78bfa',
  },
];

const SLA_COLORS = {
  withinSla: '#4ade80',
  atRisk: '#fbbf24',
  breached: '#f87171',
};

function parsePositiveInt(raw, fallback, min, max) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function formatClock(d) {
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatBusinessHours(hours) {
  if (hours == null || hours < 0) return '—';
  if (hours < 1) return '< 1h';
  return `${hours}h`;
}

function formatCreationDate(value) {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function slaBadge(status) {
  switch (status) {
    case 'BREACHED':
      return { label: 'SLA estourado', className: 'tv-badge--danger' };
    case 'AT_RISK':
      return { label: 'Em risco', className: 'tv-badge--warn' };
    case 'OK':
      return { label: 'No prazo', className: 'tv-badge--ok' };
    default:
      return { label: '—', className: 'tv-badge--muted' };
  }
}

function priorityBadgeClass(priority) {
  const p = String(priority || '').toUpperCase();
  if (p === 'CRITICAL') return 'tv-badge--critical';
  if (p === 'HIGH') return 'tv-badge--high';
  return 'tv-badge--muted';
}

function OpenTicketTable({ tickets }) {
  if (!tickets.length) {
    return <p className="tv-dashboard__empty">Nenhum ticket em aberto.</p>;
  }

  return (
    <table className="tv-attention-table">
      <colgroup>
        <col className="tv-col-protocol" />
        <col className="tv-col-subject" />
        <col className="tv-col-type" />
        <col className="tv-col-priority" />
        <col className="tv-col-status" />
        <col className="tv-col-owner" />
        <col className="tv-col-date" />
        <col className="tv-col-age" />
        <col className="tv-col-sla" />
      </colgroup>
      <thead>
        <tr>
          <th>Protocolo</th>
          <th>Assunto</th>
          <th>Tipo</th>
          <th>Prioridade</th>
          <th>Status</th>
          <th>Responsável</th>
          <th>Criação</th>
          <th title="Total de horas úteis desde a criação. Segunda a sexta, das 08:00 às 18:00. Sábado e domingo não entram na conta.">Horas úteis</th>
          <th>SLA</th>
        </tr>
      </thead>
      <tbody>
        {tickets.map((ticket) => {
          const sla = slaBadge(ticket.slaStatus);
          const protocol = formatTicketProtocol(ticket);
          return (
            <tr key={ticket.id}>
              <td className="tv-attention-protocol" title={protocol || undefined}>
                {protocol || '—'}
              </td>
              <td className="subject" title={ticket.subject}>
                {ticket.subject}
              </td>
              <td>{getDemandTypeLabel(ticket.demandType)}</td>
              <td>
                <span className={`tv-badge ${priorityBadgeClass(ticket.priority)}`}>
                  {getPriorityLabel(ticket.priority)}
                </span>
              </td>
              <td>{ticket.statusLabel || ticket.status}</td>
              <td>
                {ticket.unassigned
                  ? 'Sem responsável'
                  : (ticket.responsibleNames || []).join(', ') || '—'}
              </td>
              <td className="tv-attention-date">{formatCreationDate(ticket.creationDate)}</td>
              <td className="tv-attention-open-time" title="Horas úteis desde a criação">
                {formatBusinessHours(ticket.ageHours)}
              </td>
              <td>
                <span className={`tv-badge ${sla.className}`}>{sla.label}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const TvDashboardPage = () => {
  const [searchParams] = useSearchParams();
  const refreshMs = parsePositiveInt(searchParams.get('refresh'), DEFAULT_REFRESH_MS, 15_000, 600_000);
  const rotateMs = parsePositiveInt(searchParams.get('rotate'), DEFAULT_ROTATE_MS, 30_000, 600_000);
  const periodDays = parsePositiveInt(searchParams.get('days'), DEFAULT_PERIOD_DAYS, 7, 90);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [clock, setClock] = useState(() => new Date());
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideProgress, setSlideProgress] = useState(0);
  const [fullscreen, setFullscreen] = useState(() => !!document.fullscreenElement);
  const abortRef = useRef(null);
  const loadingRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const slide = SLIDES[slideIndex] ?? SLIDES[0];
  const showContent = Boolean(data);
  const isBusy = initialLoading || refreshing;

  const load = useCallback(async (silent = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!silent && !hasLoadedOnceRef.current) {
      setInitialLoading(true);
    } else if (silent) {
      setRefreshing(true);
    }

    try {
      const response = await getTvDashboard(periodDays, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setData(response);
      setLastUpdated(new Date());
      setError(null);
      hasLoadedOnceRef.current = true;
    } catch (err) {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      if (!silent || !hasLoadedOnceRef.current) {
        setError(err?.response?.data?.message || err?.message || 'Erro ao carregar painel');
      }
    } finally {
      if (abortRef.current === controller) {
        loadingRef.current = false;
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }, [periodDays]);

  useEffect(() => {
    load(false);
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!data) return undefined;
    const id = window.setInterval(() => load(true), refreshMs);
    return () => window.clearInterval(id);
  }, [load, refreshMs, data]);

  useEffect(() => {
    const id = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setSlideProgress(0);
    const started = Date.now();

    const progressId = window.setInterval(() => {
      const elapsed = Date.now() - started;
      setSlideProgress(Math.min(1, elapsed / rotateMs));
    }, 250);

    const advanceId = window.setTimeout(() => {
      setSlideIndex((i) => (i + 1) % SLIDES.length);
    }, rotateMs);

    return () => {
      window.clearInterval(progressId);
      window.clearTimeout(advanceId);
    };
  }, [slideIndex, rotateMs]);

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    if (!showContent) return undefined;
    const t = window.setTimeout(() => {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }, 800);
    return () => window.clearTimeout(t);
  }, [showContent]);

  const overviewGroups = useMemo(() => {
    const byType = new Map(
      (data?.demandTypeCounts || []).map((row) => [
        String(row.demandType || '').toUpperCase(),
        {
          pipeline: Number(row.pipelineCount) || 0,
          resolved: Number(row.resolvedCount) || 0,
        },
      ])
    );

    return OVERVIEW_GROUPS.map((group) => {
      const totals = group.types.reduce(
        (acc, type) => {
          const row = byType.get(type) || { pipeline: 0, resolved: 0 };
          return {
            pipeline: acc.pipeline + row.pipeline,
            resolved: acc.resolved + row.resolved,
          };
        },
        { pipeline: 0, resolved: 0 }
      );

      return {
        ...group,
        emAberto: totals.pipeline,
        resolvidos: totals.resolved,
        total: totals.pipeline + totals.resolved,
      };
    });
  }, [data]);

  const overviewChartRows = useMemo(
    () =>
      overviewGroups.map((group) => ({
        name: group.title,
        emAberto: group.emAberto,
        resolvidos: group.resolvidos,
      })),
    [overviewGroups]
  );

  const slaPieRows = useMemo(() => {
    const sla = data?.sla;
    if (!sla || sla.total === 0) return [];
    return [
      { name: 'No prazo', value: sla.withinSla, key: 'withinSla' },
      { name: 'Em risco', value: sla.atRisk, key: 'atRisk' },
      { name: 'Estourado', value: sla.breached, key: 'breached' },
    ].filter((r) => r.value > 0);
  }, [data]);

  const slaPriorityRows = useMemo(
    () =>
      (data?.sla?.byPriority || []).map((row) => ({
        name: getPriorityLabel(row.priority),
        priority: row.priority,
        'No prazo': row.withinSla,
        'Em risco': row.atRisk,
        Estourado: row.breached,
      })),
    [data]
  );

  const performerRows = useMemo(
    () =>
      (data?.topPerformers || [])
        .map((p) => ({
          name: p.name || '—',
          SUPPORT: Number(p.supportCount) || 0,
          IMPROVEMENT: Number(p.improvementCount) || 0,
          PROJECT: Number(p.projectCount) || 0,
          OTHER: Number(p.otherCount) || 0,
          total:
            Number(p.total) ||
            (Number(p.supportCount) || 0) +
              (Number(p.improvementCount) || 0) +
              (Number(p.projectCount) || 0) +
              (Number(p.otherCount) || 0),
        }))
        .filter((p) => p.total > 0)
        .slice(0, TV_PODIUM_LIMIT),
    [data]
  );

  const slaTargetsLabel = useMemo(() => {
    const targets = data?.slaTargetsHours || {};
    return Object.entries(targets)
      .map(([priority, hours]) => `${getPriorityLabel(priority)}: ${hours}h`)
      .join(' · ');
  }, [data]);

  async function toggleFullscreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen?.();
    } else {
      await document.documentElement.requestFullscreen?.();
    }
  }

  const renderSlideSkeleton = () => (
    <div className="tv-dashboard__skeleton">
      <div className="tv-dashboard__skeleton-kpis tv-overview-groups">
        {[1, 2, 3].map((item) => (
          <div key={item} className="tv-skeleton-block tv-skeleton-kpi" />
        ))}
      </div>
      <div className="tv-skeleton-block tv-skeleton-chart" />
      <p className="tv-dashboard__loading-text">Carregando indicadores do painel...</p>
    </div>
  );

  if (error && !data) {
    return (
      <div className="tv-dashboard tv-dashboard--error tv-fade-in">
        <p>{error}</p>
        <button type="button" className="tv-dashboard__fs-btn" onClick={() => load(false)}>
          Tentar novamente
        </button>
        <Link to="/home" className="tv-dashboard__fs-btn">
          Voltar ao início
        </Link>
      </div>
    );
  }

  return (
    <div className="tv-dashboard tv-fade-in" data-loading={isBusy ? '1' : '0'}>
      <header className="tv-dashboard__header">
        <div className="tv-dashboard__brand">
          <span className="tv-dashboard__company">SupportHub</span>
          <h1 className="tv-dashboard__title">{slide.title}</h1>
          <p className="tv-dashboard__subtitle">{slide.subtitle}</p>
        </div>
        <div className="tv-dashboard__meta">
          <span className="tv-dashboard__clock">{formatClock(clock)}</span>
          <span>Últimos {periodDays} dias</span>
          {refreshing ? <span className="tv-dashboard__refreshing">Atualizando...</span> : null}
          {lastUpdated ? (
            <span>
              Atualizado{' '}
              {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : initialLoading ? (
            <span>Aguardando dados...</span>
          ) : null}
          {!fullscreen ? (
            <button type="button" className="tv-dashboard__fs-btn" onClick={() => toggleFullscreen()}>
              Tela cheia
            </button>
          ) : null}
        </div>
      </header>

      <main className="tv-dashboard__main tv-slide-in" key={slide.id}>
        {!showContent ? renderSlideSkeleton() : null}

        {slide.id === 'overview' && data ? (
          <div className="tv-overview-slide">
            <div className="tv-overview-top">
            <div className="tv-overview-main">
            <div className="tv-overview-groups">
              {overviewGroups.map((group) => (
                <article
                  key={group.id}
                  className="tv-overview-group"
                  style={{ borderColor: `${group.accent}44` }}
                >
                  <header className="tv-overview-group__header">
                    <span
                      className="tv-overview-group__dot"
                      style={{ background: group.accent }}
                      aria-hidden
                    />
                    <div>
                      <h3 className="tv-overview-group__title">{group.title}</h3>
                      <p className="tv-overview-group__subtitle">{group.subtitle}</p>
                    </div>
                  </header>
                  <div className="tv-overview-group__metrics">
                    <div className="tv-overview-metric">
                      <span className="tv-overview-metric__label">Em aberto</span>
                      <strong className="tv-overview-metric__value" style={{ color: group.accent }}>
                        {group.emAberto}
                      </strong>
                    </div>
                    <div className="tv-overview-metric">
                      <span className="tv-overview-metric__label">Resolvidos</span>
                      <strong className="tv-overview-metric__value">{group.resolvidos}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="tv-overview-summary">
              <span>Total pipeline: <strong>{data.totalPipeline}</strong></span>
              <span>Resolvidos no período: <strong>{data.totalResolvedPeriod}</strong></span>
              <span>Sem responsável: <strong>{data.unassignedCount}</strong></span>
              <span>Críticos / altos: <strong>{data.criticalHighCount}</strong></span>
            </div>
            </div>

            <div className="tv-overview-chart">
              <div className="tv-dashboard__chart-body tv-overview-chart__body">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={overviewChartRows} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis
                      dataKey="name"
                      stroke="#8b93a8"
                      tick={{ fill: '#d0d6e4', fontSize: 13, fontWeight: 600 }}
                      interval={0}
                    />
                    <YAxis
                      stroke="#8b93a8"
                      tick={{ fill: '#d0d6e4', fontSize: 12 }}
                      allowDecimals={false}
                      width={40}
                      domain={[0, 'auto']}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1a1d27',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#f1f3f9',
                        fontSize: 16,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 13, paddingTop: 4 }} />
                    <Bar dataKey="emAberto" name="Em aberto" fill="#6c8cff" radius={[6, 6, 0, 0]} maxBarSize={42} />
                    <Bar dataKey="resolvidos" name="Resolvidos" fill="#4ade80" radius={[6, 6, 0, 0]} maxBarSize={42} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            </div>

            <section className="tv-overview-tickets" aria-label="Tickets em aberto">
              <h3 className="tv-overview-tickets__title">Tickets em aberto</h3>
              <div className="tv-overview-tickets__scroll">
                <OpenTicketTable tickets={data.attentionTickets || []} />
              </div>
            </section>
          </div>
        ) : null}

        {slide.id === 'sla' && data ? (
          <div className="tv-dashboard__split">
            <div className="tv-dashboard__panel">
              <h3 className="tv-dashboard__panel-title">Resumo SLA (bugs abertos)</h3>
              <div className="tv-dashboard__kpis tv-dashboard__kpis--stack">
                <article className="tv-kpi">
                  <span className="tv-kpi__label">No prazo</span>
                  <strong className="tv-kpi__value" style={{ color: SLA_COLORS.withinSla }}>
                    {data.sla?.withinSla ?? 0}
                  </strong>
                </article>
                <article className="tv-kpi tv-kpi--warn">
                  <span className="tv-kpi__label">Em risco</span>
                  <strong className="tv-kpi__value" style={{ color: SLA_COLORS.atRisk }}>
                    {data.sla?.atRisk ?? 0}
                  </strong>
                </article>
                <article className="tv-kpi tv-kpi--danger">
                  <span className="tv-kpi__label">Estourado</span>
                  <strong className="tv-kpi__value" style={{ color: SLA_COLORS.breached }}>
                    {data.sla?.breached ?? 0}
                  </strong>
                </article>
              </div>
              {slaPieRows.length > 0 ? (
                <div className="tv-sla-pie">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={slaPieRows}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {slaPieRows.map((entry) => (
                          <Cell key={entry.key} fill={SLA_COLORS[entry.key]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: '#1a1d27',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
              <div className="tv-sla-legend">
                <span>Metas em horas úteis: {slaTargetsLabel}</span>
              </div>
            </div>
            <div className="tv-dashboard__panel">
              <h3 className="tv-dashboard__panel-title">SLA por prioridade</h3>
              {slaPriorityRows.length === 0 ? (
                <p className="tv-dashboard__empty">Nenhum bug em aberto no pipeline.</p>
              ) : (
                <div className="tv-dashboard__chart-body">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={slaPriorityRows} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="name" stroke="#8b93a8" tick={{ fill: '#a8b0c4', fontSize: 13 }} />
                      <YAxis stroke="#8b93a8" tick={{ fill: '#a8b0c4', fontSize: 13 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          background: '#1a1d27',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 13 }} />
                      <Bar dataKey="No prazo" stackId="sla" fill={SLA_COLORS.withinSla} />
                      <Bar dataKey="Em risco" stackId="sla" fill={SLA_COLORS.atRisk} />
                      <Bar dataKey="Estourado" stackId="sla" fill={SLA_COLORS.breached} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {slide.id === 'performers' && data ? (
          <div className="tv-performers-slide">
            {performerRows.length === 0 ? (
              <p className="tv-dashboard__empty">
                Nenhuma resolução nos últimos {periodDays} dias com responsável atribuído.
              </p>
            ) : (
              <div className="tv-podium-list">
                {performerRows.map((performer, index) => {
                  const podium = PODIUM_RANK_META[index];
                  const rowClass = podium?.className || 'tv-podium-row--default';

                  return (
                    <article
                      key={`${performer.name}-${index}`}
                      className={`tv-podium-row ${rowClass}`}
                    >
                      <div className="tv-podium-row__rank" aria-label={`${index + 1}º lugar`}>
                        <span className="tv-podium-row__medal">{podium?.medal || `#${index + 1}`}</span>
                      </div>
                      <div className="tv-podium-row__info">
                        <strong className="tv-podium-row__name">{performer.name}</strong>
                        <div className="tv-podium-row__breakdown">
                          {PERFORMER_SERIES.filter((s) => performer[s.key] > 0).map((s) => (
                            <span key={s.key} style={{ color: DEMAND_COLORS[s.key] }}>
                              {s.label}: {performer[s.key]}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="tv-podium-row__total">
                        <strong>{performer.total}</strong>
                        <span>resolvidos</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

      </main>

      <footer className="tv-dashboard__footer">
        <div className="tv-dashboard__dots" aria-label="Slides do painel">
          {SLIDES.map((s, i) => (
            <span
              key={s.id}
              className={`tv-dashboard__dot${i === slideIndex ? ' tv-dashboard__dot--active' : ''}`}
              aria-current={i === slideIndex ? 'true' : undefined}
            />
          ))}
        </div>
        <p className="tv-dashboard__rotate-hint">
          Próximo slide em {Math.max(0, Math.ceil((rotateMs * (1 - slideProgress)) / 1000))} s
        </p>
        <div
          className="tv-dashboard__progress"
          style={{ transform: `scaleX(${slideProgress})` }}
          aria-hidden
        />
      </footer>
    </div>
  );
};

export default TvDashboardPage;
